import styles from "./index.module.scss";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import StockFundamentals from "@/components/stock-fundamentals/StockFundamentals";
import FundamentalsFlow from "@/components/stock-fundamentals/FundamentalsFlow";
import TourGuide, { useTour } from "@/components/tour/TourGuide";
import { Stock } from "@/models/stock";
import Head from "next/head";
import { buildStockOgImage } from "@/lib/stock-og";
import Tabs from "@/components/tabs/Tabs";
import { stockTradingItemIdState, textColorState, userState } from "@/lib/store";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import FeedbackForm from "@/components/feedback-form/FeedbackForm";
import { stockPageGetServerSideProps } from "@/lib/stock-page-ssr";
import { stockDescriptions, stockTitle } from "@/lib/stock-seo";
import { stockCanonicalUrl } from "@/lib/stock-url";

const tabs = [
    { title: "Visualize", value: "Visualize", badge: "NEW" },
    { title: "Summary", value: "Summary" },
    { title: "Income Statement", value: "Income Statement" },
    { title: "Balance Sheet", value: "Balance Sheet" },
    { title: "Cash Flow", value: "Cash Flow" },
    { title: "Valuation Multiples", value: "Valuation Multiples" },
    { title: "Ratios", value: "Ratios" },
];

// Shareable ?tab= codes — the first word of each tab value, lowercased
// (matches getTabCode, e.g. /fundamentals?tab=visualize, ?tab=income).
const tabValueByCode: Record<string, string> = Object.fromEntries(tabs.map((tab) => [tab.value.split(" ")[0].toLowerCase(), tab.value]));

const StockFundamentalsPage: NextPage<{ stock: Stock }> = ({ stock }) => {
    const [activeTab, setActiveTab] = useState<string>();
    const [range, setRange] = useState("a");
    const user = useAtomValue(userState);
    const [feedbackModal, setFeedbackModal] = useState(false);
    const [isObfuscated, setIsObfuscated] = useState(false);

    const textColor = useAtomValue(textColorState);

    const setStockTradingItemId = useSetAtom(stockTradingItemIdState);

    useEffect(() => {
        stock ? setStockTradingItemId(stock.tradingitem_id) : setStockTradingItemId(null);
    }, [stock]);

    const router = useRouter();
    // A valid ?tab= in the URL wins over the user's saved default tab, so
    // shared links always open on the intended tab.
    const urlTab = router.isReady ? tabValueByCode[String(router.query.tab || "").toLowerCase()] : undefined;

    // First-visit guided tour (DB-backed steps, dismissal persisted in user
    // settings). Restartable from each module's Help dialog.
    const tour = useTour("stock_fundamentals");

    const getTabCode = (tab: string) => {
        return tab && tab.split(" ")[0].toLowerCase();
    };

    const handleChange = (event: React.MouseEvent<HTMLButtonElement>, newAlignment: string) => {
        if (!newAlignment) return;
        setActiveTab(newAlignment);
        router.replace({ query: { ...router.query, tab: getTabCode(newAlignment) } }, undefined, { shallow: true });
    };

    useEffect(() => {
        if (!activeTab && router.isReady) {
            if (urlTab) {
                setActiveTab(urlTab);
            } else if (user.settings.module_settings["fundamentals->s/[exchange]/[ticker]/fundamentals"]?.activeTab) {
                setActiveTab(user.settings.module_settings["fundamentals->s/[exchange]/[ticker]/fundamentals"].activeTab);
            } else {
                setActiveTab(tabs[0]?.value);
            }
        }
    }, [activeTab, router.isReady]);

    useEffect(() => {
        if (!urlTab && user.settings.module_settings["fundamentals->s/[exchange]/[ticker]/fundamentals"]?.activeTab) setActiveTab(user.settings.module_settings["fundamentals->s/[exchange]/[ticker]/fundamentals"].activeTab);
    }, [user.settings.module_settings["fundamentals->s/[exchange]/[ticker]/fundamentals"]?.activeTab]);

    return (
        <>
            {stock &&
                (() => {
                    const { ogImageUrl, cardTitle } = buildStockOgImage(stock, "Fundamentals");
                    return (
                        <Head>
                            <title>{stockTitle(stock, "fundamentals")}</title>
                            <link rel="canonical" href={stockCanonicalUrl(stock, "fundamentals")} />
                            <meta name="keywords" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker}, Company Financials, Company Result, Book Value, DPS, EBIT, Price Book, Revenue, Payout Ratio`} />
                            <meta name="description" content={stockDescriptions.fundamentals(stock)} />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="twitter:title" content={stockTitle(stock, "fundamentals")} />
                            <meta name="twitter:description" content={stockDescriptions.fundamentals(stock)} />
                            <meta name="twitter:url" content={stockCanonicalUrl(stock, "fundamentals")} />
                            <meta name="twitter:image" content={ogImageUrl} />
                            <meta name="twitter:image:alt" content={cardTitle} />
                            <meta property="og:title" content={stockTitle(stock, "fundamentals")} />
                            <meta property="og:description" content={stockDescriptions.fundamentals(stock)} />
                            <meta property="og:url" content={stockCanonicalUrl(stock, "fundamentals")} />
                            <meta property="og:image" content={ogImageUrl} key="og:image" />
                            <meta property="og:image:width" content="1200" />
                            <meta property="og:image:height" content="630" />
                            <meta property="og:image:alt" content={cardTitle} />
                        </Head>
                    );
                })()}
            <div className={styles.StockFundamentalsPage}>
                {stock ? (
                    (() => {
                        const header = (
                            <>
                                <Box className={styles["header-container"]}>
                                    <Box className={styles["switch-container"]} data-tour="fundamentals-period">
                                        <Typography variant="body2">ANNUAL</Typography>
                                        <Switch
                                            size="small"
                                            slotProps={{ input: { "aria-label": "Quarterly figures" } }}
                                            checked={range === "q" ? true : false}
                                            onChange={() => {
                                                range === "a" ? setRange("q") : setRange("a");
                                            }}
                                            disabled={isObfuscated}
                                        />
                                        <Typography variant="body2">QUARTERLY</Typography>
                                    </Box>
                                    <Box data-tour="fundamentals-tabs" sx={{ display: "flex", minWidth: 0, flex: 1 }}>
                                        <Tabs tabs={tabs} activeTab={activeTab} module={"fundamentals"} route={"s/[exchange]/[ticker]/fundamentals"} setState={setActiveTab} handleChange={handleChange} alignment="center" setDefault />
                                    </Box>
                                    <Tooltip title="Send feedback" arrow>
                                        <IconButton aria-label="send feedback" className={styles.button} onClick={() => setFeedbackModal(true)} sx={{ mt: 0.3, width: "fit-content", alignSelf: "flex-end" }}>
                                            <FeedbackOutlinedIcon style={{ color: textColor }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <FeedbackForm isOpen={feedbackModal} onClose={setFeedbackModal} moduleId={"stock_fundamentals"} />
                            </>
                        );
                        return activeTab === "Visualize" ? (
                            <FundamentalsFlow stock={stock} range={range} helpModuleId="fundamentals_visualize" onStartTour={tour.start}>
                                {header}
                            </FundamentalsFlow>
                        ) : (
                            <StockFundamentals
                                stock={stock}
                                module={getTabCode(activeTab)}
                                key={activeTab}
                                range={range}
                                setRange={setRange}
                                setParentObfuscated={setIsObfuscated}
                                helpModuleId={`fundamentals_${getTabCode(activeTab)}`}
                                onStartTour={tour.start}
                            >
                                {header}
                            </StockFundamentals>
                        );
                    })()
                ) : (
                    <h1>Stock not found.</h1>
                )}
            </div>
            <TourGuide {...tour.props} />
        </>
    );
};

export const getServerSideProps = stockPageGetServerSideProps({ tab: "fundamentals", allowedTypes: [1] });

export default StockFundamentalsPage;
